@file:OptIn(ExperimentalMaterial3Api::class)

package ui.widget

import androidx.compose.foundation.layout.*
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import kotlinx.datetime.*
import org.ssoft.common.tools.nowDate
import provider.LocalDisplayProvider
import ssoft.frontend.generated.resources.Res
import ssoft.frontend.generated.resources.dialog__action_cancel
import ssoft.frontend.generated.resources.dialog__action_ok
import tools.EFieldRegularDate
import tools.EFieldRegularDateTime
import tools.rememberString
import kotlin.time.Clock
import kotlin.time.Duration.Companion.days
import kotlin.time.Instant

@Composable
fun DatePickerDialog(
    state: DialogState,
    date: EFieldRegularDateTime,
    enabledRange: Pair<LocalDate, LocalDate> = Clock.nowDate() to Clock.nowDate().plus(DatePeriod(days = 364)),
    onChange: () -> Unit = {}
) {
    DatePickerDialogInternal(
        state = state,
        currentDate = date.state.date,
        enabledRange = enabledRange,
        onConfirm = { newDate ->
            date.set(LocalDateTime(newDate, date.state.time))
        },
        onChange = onChange
    )
}

@Composable
fun DatePickerDialog(
    state: DialogState,
    date: EFieldRegularDate,
    enabledRange: Pair<LocalDate, LocalDate> = Clock.nowDate() to Clock.nowDate().plus(DatePeriod(days = 364)),
    onChange: () -> Unit = {}
) {
    DatePickerDialogInternal(
        state = state,
        currentDate = date.state,
        enabledRange = enabledRange,
        onConfirm = { newDate ->
            date.set(newDate)
        },
        onChange = onChange
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun DateWrapper(
    stateDialog: DialogState,
    stateDate: () -> DatePickerState,
    dismissButton: @Composable () -> Unit,
    confirmButton: @Composable (DatePickerState) -> Unit,
) {
    val isMobile = LocalDisplayProvider.current.isMobile

    Dialog(
        state = stateDialog,
        dismissOnTouchOutside = !isMobile,
        background = if (!isMobile) Color.Transparent else MaterialTheme.colorScheme.background
    ) {
        val state = remember {
            stateDate()
        }
        if (isMobile) {
            Column(
                Modifier.fillMaxSize()
                    .padding(top = 16.dp)
            ) {
                DatePicker(
                    modifier = Modifier.weight(1F).fillMaxWidth(),
                    state = state,
                )
                Row(Modifier.fillMaxWidth().padding(vertical = 16.dp, horizontal = 8.dp)) {
                    Spacer(Modifier.weight(1F))
                    dismissButton()
                    confirmButton(state)
                }
            }
        } else {
            DialogUI(
                modifier = Modifier.align(Alignment.Center),
                onDismissRequest = { stateDialog.hide() },
                dismissButton = dismissButton,
                confirmButton = {
                    confirmButton(state)
                }
            ) {
                DatePicker(
                    state = state,
                )
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun DatePickerDialogInternal(
    state: DialogState,
    currentDate: LocalDate,
    enabledRange: Pair<LocalDate, LocalDate>,
    onConfirm: (LocalDate) -> Unit,
    onChange: () -> Unit = {}
) {
    val pickerStateFun = {
        val now = Clock.nowDate()
        val yearRange = IntRange(enabledRange.first.year, enabledRange.second.year)
        val rangeMs = LongRange(
            enabledRange.first.toEpochDays().days.inWholeMilliseconds,
            enabledRange.second.toEpochDays().days.inWholeMilliseconds
        )
        DatePickerState(
            locale = CalendarLocale.current,
            initialSelectedDateMillis = currentDate.toEpochDays().days.inWholeMilliseconds,
            initialDisplayedMonthMillis = now.toEpochDays().days.inWholeMilliseconds,
            yearRange = yearRange,
            initialDisplayMode = DisplayMode.Picker,
            selectableDates = object : SelectableDates {
                override fun isSelectableDate(utcTimeMillis: Long): Boolean {
                    return utcTimeMillis in rangeMs
                }

                override fun isSelectableYear(year: Int): Boolean {
                    return year in yearRange
                }
            }
        )
    }

    DateWrapper(
        stateDialog = state,
        stateDate = pickerStateFun,
        dismissButton = {
            TextButton({ state.hide() }) {
                Text(Res.string.dialog__action_cancel.rememberString())
            }
        },
        confirmButton = { pickerState ->
            TextButton({
                pickerState.selectedDateMillis
                    ?.let { Instant.fromEpochMilliseconds(it) }
                    ?.toLocalDateTime(TimeZone.currentSystemDefault())
                    ?.date
                    ?.also { newDate ->
                        if (currentDate != newDate) {
                            onConfirm(newDate)
                            onChange()
                        }
                        state.hide()
                    } ?: state.hide()
            }) {
                Text(Res.string.dialog__action_ok.rememberString())
            }
        }
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun DatePickerDialog(
    state: DialogState,
    confirm: (LocalDate) -> Unit
) {
    val pickerStateFun = {
        val now = Clock.nowDate()
        DatePickerState(
            locale = CalendarLocale.current,
            initialDisplayedMonthMillis = now.toEpochDays().days.inWholeMilliseconds,
            initialDisplayMode = DisplayMode.Picker
        )
    }

    DateWrapper(
        stateDialog = state,
        stateDate = pickerStateFun,
        dismissButton = {
            TextButton({ state.hide() }) {
                Text(Res.string.dialog__action_cancel.rememberString())
            }
        },
        confirmButton = { pickerState ->
            TextButton({
                pickerState.selectedDateMillis
                    ?.let { Instant.fromEpochMilliseconds(it) }
                    ?.toLocalDateTime(TimeZone.currentSystemDefault())
                    ?.date
                    ?.also { newDate ->
                        confirm(newDate)
                        state.hide()
                    } ?: state.hide()
            }) {
                Text(Res.string.dialog__action_ok.rememberString())
            }
        }
    )
}
